import React, { useEffect, useState } from 'react';
import { RefreshCw, Lock, Send, UserSearch, ShieldAlert } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { StatusPill, StatusFilter, HistoryList, fmtDate } from './AdminModeration';

const STATUSES = ['open', 'in_progress', 'resolved', 'closed', 'all'];

const EmailBadge = ({ status }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full ${status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid={`email-status-${status}`}>
    email {status}
  </span>
);

export const TicketDetail = ({ ticket, onUpdated, onOpenMember }) => {
  const [status, setStatus] = useState(ticket.status);
  const [note, setNote] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState('');
  const { toast } = useToast();
  const fail = (e) => toast({ title: 'Action failed', description: formatApiError(e), variant: 'destructive' });

  const saveStatus = async () => {
    setBusy('status');
    try { await apiService.admin.updateTicket(ticket.id, { status, resolution_note: ticket.resolution_note || null }); toast({ title: 'Status updated' }); onUpdated(); } catch (e) { fail(e); } finally { setBusy(''); }
  };
  const addNote = async () => {
    setBusy('note');
    try { await apiService.admin.addTicketNote(ticket.id, note); setNote(''); toast({ title: 'Internal note added' }); onUpdated(); } catch (e) { fail(e); } finally { setBusy(''); }
  };
  const sendReply = async (resolve) => {
    setBusy('reply');
    try {
      const r = await apiService.admin.replyTicket(ticket.id, reply, resolve);
      const ok = r.data.data.reply.email_status === 'accepted';
      toast({ title: ok ? 'Reply emailed to requester' : 'Reply saved — email NOT delivered', description: ok ? `Message id ${r.data.data.reply.email_message_id || ''}` : r.data.data.reply.email_error, variant: ok ? 'default' : 'destructive' });
      setReply(''); onUpdated();
    } catch (e) { fail(e); } finally { setBusy(''); }
  };

  return (
    <div className="mt-4 border-t pt-4 space-y-5 text-sm" data-testid={`ticket-detail-${ticket.id}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><p className="text-gray-500">From</p><p className="font-medium">{ticket.name || '—'} <span className="text-gray-400 break-all">{ticket.email}</span></p>
          {ticket.member_id ? (
            <Button size="sm" variant="outline" className="mt-1" onClick={() => onOpenMember?.(ticket.member_id, 'support')} data-testid={`ticket-open-member-${ticket.id}`}><UserSearch className="w-3.5 h-3.5 mr-1" />Open member{ticket.member_email_verified ? '' : ' (email unverified)'}</Button>
          ) : <p className="text-xs text-gray-400 mt-1" data-testid={`ticket-no-member-${ticket.id}`}>No registered member with this email</p>}
          {ticket.spam_score >= 2 && <p className="text-xs text-amber-700 mt-1 inline-flex items-center" data-testid={`ticket-spam-flag-${ticket.id}`}><ShieldAlert className="w-3.5 h-3.5 mr-1" />Possible spam: {(ticket.spam_signals || []).join(', ')}</p>}
        </div>
        <div><p className="text-gray-500">Kind / topic</p><p className="font-medium">{ticket.kind} · {ticket.subject || ticket.category}</p></div>
        <div><p className="text-gray-500">Confirmation email</p><p>{ticket.confirmation_email ? <EmailBadge status={ticket.confirmation_email.status} /> : <span className="text-gray-400">n/a (older ticket)</span>}</p></div>
      </div>
      <div><p className="text-gray-500">Message</p><p className="whitespace-pre-wrap bg-gray-50 rounded p-2" data-testid="ticket-message">{ticket.message || ticket.description}</p>{ticket.reported_user_name && <p className="text-xs text-gray-500 mt-1">Mentions member: {ticket.reported_user_name}</p>}</div>

      <div data-testid="ticket-replies">
        <p className="text-gray-500 mb-1 flex items-center"><Send className="w-3 h-3 mr-1" />Customer-facing replies ({ticket.replies?.length || 0})</p>
        <ul className="space-y-2">{(ticket.replies || []).map((r) => <li key={r.id} className="bg-blue-50 rounded p-2"><div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-1"><span>{fmtDate(r.at)}</span><b>{r.admin_username}</b><EmailBadge status={r.email_status} />{r.email_error && <span className="text-red-600">{r.email_error}</span>}</div><p className="whitespace-pre-wrap">{r.message}</p></li>)}</ul>
        <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply — this WILL be emailed to the requester" rows={3} className="mt-2" data-testid="ticket-reply-input" />
        <div className="flex gap-2 mt-2">
          <Button size="sm" onClick={() => sendReply(false)} disabled={!reply.trim() || busy} data-testid="ticket-reply-send-btn">{busy === 'reply' ? 'Sending…' : 'Send reply'}</Button>
          <Button size="sm" variant="outline" onClick={() => sendReply(true)} disabled={!reply.trim() || busy} data-testid="ticket-reply-resolve-btn">Send & resolve</Button>
        </div>
      </div>

      <div data-testid="ticket-internal-notes">
        <p className="text-gray-500 mb-1 flex items-center"><Lock className="w-3 h-3 mr-1" />Internal notes — private, never emailed ({ticket.internal_notes?.length || 0})</p>
        <ul className="space-y-1">{(ticket.internal_notes || []).map((n) => <li key={n.id} className="bg-amber-50 rounded p-2"><span className="text-xs text-gray-500">{fmtDate(n.at)} · <b>{n.admin_username}</b></span><p className="whitespace-pre-wrap">{n.note}</p></li>)}</ul>
        <div className="flex gap-2 mt-2 items-start">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private internal note" rows={2} data-testid="ticket-note-input" />
          <Button size="sm" variant="secondary" onClick={addNote} disabled={!note.trim() || busy} data-testid="ticket-note-add-btn">{busy === 'note' ? 'Saving…' : 'Add note'}</Button>
        </div>
      </div>

      <div><p className="text-gray-500 mb-1">History</p><HistoryList items={ticket.history} /></div>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-md px-3 py-2 bg-white" data-testid="ticket-status-select">
          {['open', 'in_progress', 'resolved', 'closed'].map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <Button size="sm" onClick={saveStatus} disabled={busy || status === ticket.status} data-testid="ticket-status-save-btn">{busy === 'status' ? 'Saving…' : 'Update status'}</Button>
      </div>
    </div>
  );
};

const AdminTickets = ({ onOpenMember }) => {
  const [filter, setFilter] = useState('open');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = () => apiService.admin.getTickets({ status_filter: filter, limit: 100 }).then((r) => { setData(r.data.data); setError(''); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-4" data-testid="admin-tickets">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusFilter value={filter} onChange={setFilter} options={STATUSES} testId="tickets-filter" />
        <div className="flex items-center gap-3 text-sm text-gray-600">{data && <span data-testid="tickets-total">{data.total} ticket{data.total === 1 ? '' : 's'}</span>}<Button size="sm" variant="outline" onClick={load} data-testid="tickets-refresh"><RefreshCw className="w-4 h-4" /></Button></div>
      </div>
      {error && <p className="text-red-600" data-testid="tickets-error">{error}</p>}
      {data && data.tickets.length === 0 && <Card className="p-6 text-center text-gray-500" data-testid="tickets-empty">No {filter === 'all' ? '' : filter.replace('_', ' ')} tickets.</Card>}
      {data?.tickets.map((t) => (
        <Card key={t.id} className="p-4" data-testid={`ticket-row-${t.id}`}>
          <button type="button" className="w-full text-left flex flex-wrap items-center gap-3" onClick={() => setOpenId(openId === t.id ? null : t.id)} data-testid={`ticket-toggle-${t.id}`}>
            <StatusPill status={t.status} />
            <span className="font-mono text-xs text-gray-500">#{t.id.slice(0, 8)}</span>
            <span className="font-semibold">{t.subject || t.category}</span>
            <span className="text-gray-600">{t.name || t.email}</span>
            {t.replies?.length > 0 && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{t.replies.length} repl{t.replies.length === 1 ? 'y' : 'ies'}</span>}
            <span className="ml-auto text-xs text-gray-400">{fmtDate(t.created_at)}</span>
          </button>
          {openId === t.id && <TicketDetail ticket={t} onUpdated={load} onOpenMember={onOpenMember} />}
        </Card>
      ))}
    </div>
  );
};

export default AdminTickets;
